import { CartAddEvent } from '@theme/events';

const SESSION_KEY_PREFIX = 'drw_overlay_seen_';
const HOST_CLASS = 'cart-drawer-overlay-host';

class CartDrawerOverlayManager {
  constructor() {
    /** @type {HTMLElement | null} */
    this.dataRoot = document.getElementById('cart-drawer-overlays-data');
    if (!this.dataRoot) return;

    this.oncePerSession = this.dataRoot.dataset.oncePerSession === 'true';

    /** @type {Map<string, HTMLTemplateElement>} */
    this.templatesByTrigger = new Map();
    for (const tpl of this.dataRoot.querySelectorAll('template.cart-drawer-overlay-template')) {
      const ids = (tpl.dataset.triggerProductIds || '').split(',').map((s) => s.trim()).filter(Boolean);
      for (const id of ids) this.templatesByTrigger.set(id, tpl);
    }

    document.addEventListener(CartAddEvent.eventName, this.handleCartEvent);

    this.lastSeenProductId = null;
  }

  handleCartEvent = (event) => {
    const productId = event?.detail?.data?.productId;
    if (!productId) return;
    if (productId === this.lastSeenProductId) return;
    this.lastSeenProductId = productId;

    const tpl = this.templatesByTrigger.get(String(productId));
    if (!tpl) return;

    const blockId = tpl.dataset.blockId;
    if (this.oncePerSession && sessionStorage.getItem(SESSION_KEY_PREFIX + blockId)) return;

    // Wait one frame so cart-drawer's auto-open finishes mounting the dialog.
    requestAnimationFrame(() => this.showOverlay(tpl));
  };

  showOverlay(template) {
    const drawer = document.querySelector('cart-drawer-component .cart-drawer__inner');
    if (!drawer) return;

    let host = drawer.querySelector('.' + HOST_CLASS);
    if (!host) {
      host = document.createElement('div');
      host.className = HOST_CLASS;
      host.setAttribute('aria-hidden', 'true');
      drawer.appendChild(host);
    }

    host.innerHTML = '';
    const fragment = template.content.cloneNode(true);
    host.appendChild(fragment);
    host.dataset.blockId = template.dataset.blockId;
    host.dataset.overlayType = template.dataset.overlayType;
    host.setAttribute('aria-hidden', 'false');

    if (template.dataset.overlayType === 'bundle') {
      this.bindBundle(host, template);
    }

    this.bindActions(host);

    // Make sure cart drawer is visible
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      const dialog = cartDrawer.querySelector('.cart-drawer__dialog');
      if (dialog && !dialog.open) cartDrawer.open();
    }
  }

  hideOverlay(host) {
    if (!host) return;
    const blockId = host.dataset.blockId;
    if (this.oncePerSession && blockId) {
      sessionStorage.setItem(SESSION_KEY_PREFIX + blockId, '1');
    }
    host.setAttribute('aria-hidden', 'true');
    host.innerHTML = '';
  }

  bindActions(host) {
    host.addEventListener('click', async (e) => {
      const actionEl = e.target.closest('[data-overlay-action]');
      if (!actionEl) return;
      const action = actionEl.dataset.overlayAction;

      if (action === 'dismiss') {
        this.hideOverlay(host);
        return;
      }

      if (action === 'add') {
        const variantIds = (actionEl.dataset.variantIds || '').split(',').map((s) => s.trim()).filter(Boolean);
        if (!variantIds.length) return;
        await this.addVariants(actionEl, variantIds);
        this.hideOverlay(host);
      }

      if (action === 'add-bundle') {
        const checked = host.querySelectorAll('.cart-drawer-overlay__checkbox:checked');
        const ids = Array.from(checked).map((c) => c.dataset.variantId).filter(Boolean);
        if (!ids.length) return;
        await this.addVariants(actionEl, ids);
        this.hideOverlay(host);
      }
    });
  }

  async addVariants(button, variantIds) {
    const original = button.innerHTML;
    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const items = variantIds.map((id) => ({ id: Number(id), quantity: 1 }));
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) throw new Error('Add to cart failed');
      // Notify the rest of the theme (refreshes drawer contents).
      document.dispatchEvent(new CartAddEvent(undefined, undefined, { source: 'cart-drawer-overlay' }));
    } catch (err) {
      console.error('[cart-drawer-overlay] Add failed', err);
      button.innerHTML = original;
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  bindBundle(host, template) {
    const tierPercents = {
      2: parseFloat(template.dataset.pct2 || '0'),
      3: parseFloat(template.dataset.pct3 || '0'),
      4: parseFloat(template.dataset.pct4 || '0'),
    };
    const ctaTpl = template.dataset.ctaTemplate || 'Add %count% items for %total%';

    const checkboxes = Array.from(host.querySelectorAll('.cart-drawer-overlay__checkbox'));
    const totalEl = host.querySelector('[data-bundle-total]');
    const compareEl = host.querySelector('[data-bundle-compare]');
    const ctaLabel = host.querySelector('.cart-drawer-overlay__cta-label');
    const ctaButton = host.querySelector('[data-overlay-action="add-bundle"]');

    /** Tier % for a given checked count (clamped to 4). */
    const tierFor = (count) => tierPercents[Math.min(count, 4)] || 0;

    /** Apply percent-off discount to a cents value. Pure function. */
    const applyDiscount = (cents, pct) =>
      pct > 0 ? Math.round(cents * (1 - pct / 100)) : cents;

    /** Read a row's prices (in cents) from its checkbox dataset. */
    const readRow = (cb) => ({
      checked: cb.checked,
      price: Number(cb.dataset.price || 0),
      compareAt: Number(cb.dataset.compareAt || 0),
      li: cb.closest('.cart-drawer-overlay__item'),
    });

    /** What this row should display, given the current tier %. */
    const computeRow = (row, pct) => {
      const final = row.checked ? applyDiscount(row.price, pct) : row.price;
      // Strike = compare_at if it's higher than final; else show row.price as
      // strike only when a tier discount is actually being applied.
      let strike = 0;
      if (row.compareAt > final) strike = row.compareAt;
      else if (row.checked && pct > 0 && row.price > final) strike = row.price;
      return { final, strike };
    };

    /** Write final + strike text into the row's DOM. */
    const renderRow = (row, { final, strike }) => {
      const finalEl = row.li?.querySelector('[data-item-discounted]');
      const strikeEl = row.li?.querySelector('[data-item-compare]');
      if (finalEl) finalEl.textContent = formatMoney(final);
      if (strikeEl) {
        strikeEl.hidden = strike <= 0;
        if (strike > 0) strikeEl.textContent = formatMoney(strike);
      }
    };

    const recompute = () => {
      const rows = checkboxes.map(readRow);
      const checkedCount = rows.filter((r) => r.checked).length;
      const pct = tierFor(checkedCount);

      let totalFinal = 0;
      let totalStrike = 0;

      for (const row of rows) {
        const { final, strike } = computeRow(row, pct);
        renderRow(row, { final, strike });
        if (row.checked) {
          totalFinal += final;
          totalStrike += row.compareAt > 0 ? row.compareAt : row.price;
        }
      }

      if (totalEl) totalEl.textContent = formatMoney(totalFinal);
      if (compareEl) {
        compareEl.hidden = totalStrike <= totalFinal;
        if (totalStrike > totalFinal) compareEl.textContent = formatMoney(totalStrike);
      }
      if (ctaLabel) {
        ctaLabel.textContent = ctaTpl
          .replaceAll('%count%', String(checkedCount))
          .replaceAll('%total%', formatMoney(totalFinal));
      }
      if (ctaButton) ctaButton.disabled = checkedCount === 0;
    };

    checkboxes.forEach((c) => c.addEventListener('change', recompute));
    recompute();
  }
}

/**
 * Lightweight money formatter that mirrors Shopify's default.
 * Reads currency code from window.Shopify if available; falls back to USD.
 * @param {number} cents
 */
function formatMoney(cents) {
  try {
    const code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
    }).format(cents / 100);
  } catch (_e) {
    return '$' + (cents / 100).toFixed(2);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new CartDrawerOverlayManager());
} else {
  new CartDrawerOverlayManager();
}
